import {
  Body1,
  Body1Strong,
  Button,
  Card,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title1,
  Title2,
} from '@fluentui/react-components';
import { ErrorCircleRegular } from '@fluentui/react-icons';
import { useSearchParams } from 'react-router-dom';

import { useDocumentTitle } from '@shared/hooks/useDocumentTitle.hook';
import { EAiMode } from '@modules/ai/types';
import useAiCreditRates from './api';
import { PriceCategory, toPriceCategories } from './creditRates';
import { calculateTokensPerCredit, getLatestCreditRateVersion, selectCreditRateVersion } from './creditRates.helpers';

const creditFormatter = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 6,
});

const tokenFormatter = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 2,
});

const wholeNumberFormatter = new Intl.NumberFormat('en-GB');

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

const formatUpdatedAt = (updatedAt: string): string => dateFormatter.format(new Date(`${updatedAt}T00:00:00Z`));

// Numeric columns read as a column of figures when right-aligned with tabular digits.
const NUMERIC_CELL_CLASS = '!justify-end tabular-nums';

type PricingTableProps = {
  title: string;
  description: string;
  categories: readonly PriceCategory[];
};

const PricingTable = ({ title, description, categories }: PricingTableProps) => {
  const headingId = `${title.toLowerCase().replace(/ /g, '-')}-heading`;

  return (
    <section aria-labelledby={headingId}>
      <div className="mb-3">
        <Title2 as="h2" id={headingId}>
          {title}
        </Title2>
        <Body1 className="mt-2 pl-2 block text-neutralFg-3">{description}</Body1>
      </div>

      <Card className="!block !overflow-hidden !p-0 shadow-sm">
        <div className="overflow-x-auto">
          <Table aria-label={`${title} credit rates`} className="min-w-[600px]">
            <TableHeader>
              <TableRow className="bg-neutralBg-3">
                <TableHeaderCell>
                  <Body1Strong>Token category</Body1Strong>
                </TableHeaderCell>
                <TableHeaderCell className={NUMERIC_CELL_CLASS}>
                  <Body1Strong>AI Credits per 1M tokens</Body1Strong>
                </TableHeaderCell>
                <TableHeaderCell className={NUMERIC_CELL_CLASS}>
                  <Body1Strong>Tokens per AI Credit</Body1Strong>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((category) => {
                return (
                  <TableRow key={category.id} className="transition-colors hover:bg-neutralBg-2">
                    <TableCell>
                      <Body1Strong>{category.label}</Body1Strong>
                    </TableCell>
                    <TableCell className={NUMERIC_CELL_CLASS}>
                      {creditFormatter.format(category.creditsPerMillionTokens)}
                    </TableCell>
                    <TableCell className={NUMERIC_CELL_CLASS}>
                      {tokenFormatter.format(calculateTokensPerCredit(category.creditsPerMillionTokens))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </section>
  );
};

const AiCredits = () => {
  useDocumentTitle('AI Credit rates | Confideck');

  const [searchParams, setSearchParams] = useSearchParams();
  const creditRates = useAiCreditRates();
  const latestVersion = creditRates.data ? getLatestCreditRateVersion(creditRates.data) : undefined;
  const selection = creditRates.data
    ? selectCreditRateVersion(searchParams.get('version'), creditRates.data)
    : undefined;

  return (
    <div className="min-h-full overflow-y-auto bg-neutralBg-3 text-neutralFg-1">
      <header className="sticky top-0 z-10 border-b border-neutralStroke-2 bg-neutralBg-1/90 backdrop-blur">
        <div className="mx-auto flex min-h-14 max-w-6xl items-center px-4 py-2 sm:px-6 lg:px-8">
          <img src="/favicon/svg.svg" alt="" className="mr-2 h-8 w-8" />
          <Body1Strong className="text-lg">Confideck</Body1Strong>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <div className="mb-10 max-w-3xl">
          <Title1 as="h1">AI Credit rates</Title1>
        </div>

        {!creditRates.data && creditRates.isPending ? (
          <Spinner aria-label="Loading AI credit rates" size="large" />
        ) : !creditRates.data && creditRates.isError ? (
          <Card role="alert" className="max-w-2xl !flex-row !items-start !gap-4 !p-6 shadow-sm">
            <ErrorCircleRegular className="mt-1 shrink-0 text-brand-80" fontSize="28px" />
            <div>
              <Title2 as="h2">Credit rates could not be loaded</Title2>
              <Body1 className="mt-2 block text-neutralFg-3">Please try again.</Body1>
              <Button className="mt-5" appearance="primary" onClick={() => void creditRates.refetch()}>
                Retry
              </Button>
            </div>
          </Card>
        ) : !selection || !latestVersion ? null : selection.status === 'invalid' ? (
          <Card role="alert" className="max-w-2xl !flex-row !items-start !gap-4 !p-6 shadow-sm">
            <ErrorCircleRegular className="mt-1 shrink-0 text-brand-80" fontSize="28px" />
            <div>
              <Title2 as="h2">Pricing version not found</Title2>
              <Body1 className="mt-2 block text-neutralFg-3">
                {selection.requestedVersion
                  ? `“${selection.requestedVersion}” is not an available integer pricing version.`
                  : 'The version query parameter is missing a version number.'}
              </Body1>
              <Button className="mt-5" appearance="primary" onClick={() => setSearchParams({})}>
                Open latest version
              </Button>
            </div>
          </Card>
        ) : (
          <>
            <div className="mb-8 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-neutralStroke-2 pb-5">
              <Body1Strong>Version {selection.rateVersion.version}</Body1Strong>
              {selection.rateVersion.version === latestVersion.version && (
                <span className="rounded-full bg-brand-160 px-2.5 py-0.5 text-xs font-semibold text-brand-30">
                  Latest
                </span>
              )}
              <span aria-hidden className="text-neutralFg-4">
                ·
              </span>
              <Body1 className="text-neutralFg-3">Updated on {formatUpdatedAt(selection.rateVersion.updatedAt)}</Body1>
            </div>

            <div className="space-y-10">
              <PricingTable
                title="Standard AI"
                description={`Short context covers up to ${wholeNumberFormatter.format(selection.rateVersion.shortContextMaxInputTokens)} input tokens; long context applies above ${wholeNumberFormatter.format(selection.rateVersion.shortContextMaxInputTokens)}.`}
                categories={toPriceCategories(selection.rateVersion, EAiMode.STANDARD)}
              />
              <PricingTable
                title="Advanced AI"
                description={`Short context covers up to ${wholeNumberFormatter.format(selection.rateVersion.shortContextMaxInputTokens)} input tokens; long context applies above ${wholeNumberFormatter.format(selection.rateVersion.shortContextMaxInputTokens)}.`}
                categories={toPriceCategories(selection.rateVersion, EAiMode.ADVANCED)}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AiCredits;
